import { useNavigate } from "react-router-dom";
import { Minus, Plus, X, ShoppingBag } from "lucide-react";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from "@/components/ui/sheet";
import { useCart } from "@/context/CartContext";

export const CartDrawer = () => {
  const { open, setOpen, setReserveOpen, items, updateQty, removeItem, subtotal, keyOf, count } = useCart();
  const navigate = useNavigate();

  const goCheckout = () => {
    setOpen(false);
    setReserveOpen(true);
  };

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetContent
        data-testid="cart-drawer"
        className="w-full sm:max-w-md flex flex-col p-0 gap-0"
      >
        <SheetHeader className="px-6 py-5 border-b border-zinc-100">
          <SheetTitle className="font-display text-lg font-bold tracking-tight flex items-center gap-2">
            Your Reservations
            <span className="text-sm font-normal text-zinc-400">({count})</span>
          </SheetTitle>
          <SheetDescription className="sr-only">Your reserved items</SheetDescription>
        </SheetHeader>

        {items.length === 0 ? (
          <div
            data-testid="cart-empty"
            className="flex-1 flex flex-col items-center justify-center text-center px-6 gap-3"
          >
            <div className="w-14 h-14 rounded-full bg-zinc-100 flex items-center justify-center">
              <ShoppingBag className="w-6 h-6 text-zinc-400" />
            </div>
            <p className="text-zinc-900 font-medium">No reservations yet</p>
            <p className="text-sm text-zinc-500">Reserve a pair of Vsense glasses — it's free.</p>
            <button
              data-testid="cart-shop-btn"
              onClick={() => {
                setOpen(false);
                navigate("/shop");
              }}
              className="mt-2 bg-zinc-900 text-white text-sm font-medium px-6 py-3 rounded-full hover:bg-zinc-800 active:scale-[0.98] transition-all duration-200"
            >
              Shop AI Glasses
            </button>
          </div>
        ) : (
          <>
            <div className="flex-1 overflow-y-auto px-6 py-4 space-y-5">
              {items.map((it) => {
                const k = keyOf(it);
                return (
                  <div key={k} data-testid={`cart-item-${it.product_id}-${it.color}`} className="flex gap-4">
                    <div className="w-20 h-20 rounded-2xl overflow-hidden bg-[#F5F5F7] shrink-0">
                      <img src={it.image} alt={it.product_name} className="w-full h-full object-cover" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-2">
                        <div>
                          <p className="text-sm font-semibold text-zinc-900">{it.product_name}</p>
                          <p className="text-xs text-zinc-500">{[it.color, it.shape, it.lens].filter(Boolean).join(" · ")}</p>
                        </div>
                        <button
                          data-testid={`cart-remove-${it.product_id}-${it.color}`}
                          onClick={() => removeItem(k)}
                          className="text-zinc-400 hover:text-zinc-900 transition-colors"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                      <div className="mt-3 flex items-center justify-between">
                        <div className="flex items-center border border-zinc-200 rounded-full">
                          <button
                            data-testid={`cart-dec-${it.product_id}-${it.color}`}
                            onClick={() => updateQty(k, it.quantity - 1)}
                            className="p-1.5 text-zinc-600 hover:text-zinc-900 transition-colors"
                          >
                            <Minus className="w-3.5 h-3.5" />
                          </button>
                          <span className="px-2 text-sm w-7 text-center">{it.quantity}</span>
                          <button
                            data-testid={`cart-inc-${it.product_id}-${it.color}`}
                            onClick={() => updateQty(k, it.quantity + 1)}
                            className="p-1.5 text-zinc-600 hover:text-zinc-900 transition-colors"
                          >
                            <Plus className="w-3.5 h-3.5" />
                          </button>
                        </div>
                        <span className="text-sm font-semibold text-zinc-900">
                          ${(it.unit_price * it.quantity).toFixed(2)}
                        </span>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            <div className="border-t border-zinc-100 px-6 py-5 space-y-4">
              <div className="flex items-center justify-between">
                <span className="text-sm text-zinc-500">Estimated total</span>
                <span data-testid="cart-subtotal" className="font-display text-lg font-bold tracking-tight">
                  ${subtotal.toFixed(2)}
                </span>
              </div>
              <p className="-mt-2 text-xs text-zinc-400">Reserve free · you won't be charged today.</p>
              <button
                data-testid="cart-checkout-btn"
                onClick={goCheckout}
                className="w-full bg-zinc-900 text-white text-sm font-medium py-3.5 rounded-full hover:bg-zinc-800 active:scale-[0.99] transition-all duration-200"
              >
                Reserve now
              </button>
              <button
                data-testid="cart-continue-btn"
                onClick={() => setOpen(false)}
                className="w-full text-sm text-zinc-600 hover:text-zinc-900 transition-colors"
              >
                Continue shopping
              </button>
            </div>
          </>
        )}
      </SheetContent>
    </Sheet>
  );
};
